/**
 * Notifications Page
 * Full page view of all notifications with filtering and management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  ArrowLeft,
  Check,
  CheckCheck,
  Trash2,
  Filter,
  Loader2,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Info,
  Megaphone,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
  dismissNotification,
  Notification,
  NotificationType,
} from '../services/notificationService';
import { getErrorMessage } from '../services/apiService';

type FilterType = 'all' | 'unread' | 'read';

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getNotifications(true, 100); // Get all including read
      setNotifications(response.notifications);
    } catch (err) {
      setError(getErrorMessage(err));
      console.error('Error fetching notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notification: Notification) => {
    if (notification.isRead) return;
    try {
      await markAsRead(notification.id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    setIsMarkingAllRead(true);
    try {
      await markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Error marking all as read:', err);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  const handleDismiss = async (notification: Notification) => {
    try {
      await dismissNotification(notification.id);
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  };

  const handleActionClick = (notification: Notification) => {
    if (notification.actionUrl) {
      // Mark as read first
      if (!notification.isRead) {
        handleMarkAsRead(notification);
      }

      // Check if internal route, static file, or external URL
      if (notification.actionUrl.startsWith('/')) {
        // Static files (like .html) need a full page load, not React Router
        if (notification.actionUrl.includes('.')) {
          window.location.href = notification.actionUrl;
        } else {
          navigate(notification.actionUrl);
        }
      } else {
        window.open(notification.actionUrl, '_blank');
      }
    }
  };

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'Warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'Error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'Success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'Announcement':
        return <Megaphone className="w-5 h-5 text-purple-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getTypeBadgeColor = (type: NotificationType) => {
    switch (type) {
      case 'Warning':
        return 'bg-amber-100 text-amber-800';
      case 'Error':
        return 'bg-red-100 text-red-800';
      case 'Success':
        return 'bg-green-100 text-green-800';
      case 'Announcement':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filter === 'unread') return !n.isRead;
    if (filter === 'read') return n.isRead;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navigation />

      <main className="flex-grow pt-28 md:pt-32 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 text-sm font-medium text-white bg-[#c41e3a] hover:bg-[#a01830] rounded-lg transition-colors"
              >
                Done
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Bell className="w-8 h-8 text-[#c41e3a]" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
                  <p className="text-sm text-gray-500">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={fetchNotifications}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>

                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={isMarkingAllRead}
                    className="flex items-center px-4 py-2 text-sm font-medium text-[#c41e3a] bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isMarkingAllRead ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCheck className="w-4 h-4 mr-2" />
                    )}
                    Mark all read
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-2 mb-6">
            <Filter className="w-4 h-4 text-gray-400" />
            <div className="flex bg-gray-100 rounded-lg p-1">
              {(['all', 'unread', 'read'] as FilterType[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    filter === f
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  {f === 'unread' && unreadCount > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-[#c41e3a] text-white rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-700">{error}</p>
              <button
                onClick={fetchNotifications}
                className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No notifications</h3>
              <p className="text-gray-500">
                {filter === 'unread'
                  ? "You're all caught up!"
                  : filter === 'read'
                  ? 'No read notifications'
                  : 'No notifications yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`bg-white rounded-lg shadow-sm border transition-all hover:shadow-md ${
                    notification.isRead ? 'border-gray-200' : 'border-l-4 border-l-[#c41e3a] border-gray-200'
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="flex-shrink-0 mt-0.5">{getTypeIcon(notification.type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3
                              className={`text-sm font-semibold ${
                                notification.isRead ? 'text-gray-700' : 'text-gray-900'
                              }`}
                            >
                              {notification.title}
                            </h3>
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadgeColor(
                                notification.type
                              )}`}
                            >
                              {notification.type}
                            </span>
                            {!notification.isRead && (
                              <span className="w-2 h-2 bg-[#c41e3a] rounded-full" />
                            )}
                          </div>
                          <p
                            className={`text-sm ${
                              notification.isRead ? 'text-gray-500' : 'text-gray-700'
                            }`}
                          >
                            {notification.message}
                          </p>
                          <div className="flex items-center space-x-4 mt-2">
                            <span className="text-xs text-gray-400">
                              {formatDate(notification.createdAt)}
                            </span>
                            {notification.scope !== 'System' && (
                              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                                {notification.scope}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-1 ml-4">
                        {!notification.isRead && (
                          <button
                            onClick={() => handleMarkAsRead(notification)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDismiss(notification)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Dismiss"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {notification.actionUrl && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => handleActionClick(notification)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-[#c41e3a] bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          {notification.actionLabel || 'View Details'}
                          {!notification.actionUrl.startsWith('/') && (
                            <ExternalLink className="w-3 h-3 ml-1.5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Notifications;
